// ============================================================================
// Policy Acknowledgment Component
// Displays policies/notices with read confirmation and e-signature
// ============================================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  FileText,
  Check,
  AlertTriangle,
  Clock,
  Shield,
  Eye,
  Scroll,
  BookOpen,
  ChevronDown,
  CheckCircle2,
  XCircle,
  Pen,
  Download,
  Printer,
  Info,
} from 'lucide-react';
import ESignature, { SignatureDisplay } from './ESignature';
import { v4 as uuidv4 } from 'uuid';

// ============================================================================
// POLICY READER COMPONENT (Read & Scroll Tracking)
// ============================================================================

function PolicyReader({
  content,
  title,
  onReadComplete,
  readComplete,
  requireFullScroll = true,
}) {
  const scrollRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(!requireFullScroll);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
    setScrollProgress(Math.min(100, Math.max(0, progress)));

    // Consider fully scrolled if within 20px of bottom
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToBottom(true);
      onReadComplete?.();
    }
  }, [onReadComplete]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  return (
    <div className="space-y-3">
      {/* Scroll Progress */}
      {requireFullScroll && !readComplete && (
        <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
          <Scroll className="h-4 w-4 text-blue-600" />
          <div className="flex-1">
            <Progress value={scrollProgress} className="h-2" />
          </div>
          <span className="text-xs text-blue-600 font-medium">
            {Math.round(scrollProgress)}%
          </span>
        </div>
      )}

      {/* Content Area */}
      <div
        ref={scrollRef}
        className="max-h-96 overflow-y-auto border rounded-lg bg-white"
      >
        <div className="p-4">
          {title && (
            <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b">
              {title}
            </h3>
          )}
          <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-line">
            {content}
          </div>
        </div>
      </div>

      {/* Scroll Hint */}
      {requireFullScroll && !hasScrolledToBottom && (
        <div className="flex items-center justify-center gap-2 text-sm text-blue-600 animate-bounce">
          <ChevronDown className="h-4 w-4" />
          <span>Scroll to read the entire document</span>
          <ChevronDown className="h-4 w-4" />
        </div>
      )}

      {/* Read Complete Badge */}
      {hasScrolledToBottom && (
        <div className="flex items-center justify-center">
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <Check className="h-3 w-3 mr-1" />
            Document Read
          </Badge>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SINGLE POLICY ACKNOWLEDGMENT CARD
// ============================================================================

export function PolicyAcknowledgmentCard({
  policy,
  employeeId,
  employeeName,
  employeeEmail,
  onAcknowledge,
  existingAcknowledgment,
  disabled = false,
}) {
  const [isReading, setIsReading] = useState(false);
  const [hasRead, setHasRead] = useState(!!existingAcknowledgment);
  const [showSignature, setShowSignature] = useState(false);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [acknowledged, setAcknowledged] = useState(!!existingAcknowledgment);

  const handleAcknowledge = async (signatureData) => {
    setIsAcknowledging(true);
    try {
      const acknowledgmentData = {
        id: uuidv4(),
        policyCode: policy.policyCode,
        policyTitle: policy.title,
        policyVersion: policy.version,
        employeeId,
        employeeName,
        employeeEmail,
        acknowledgedAt: new Date().toISOString(),
        signature: policy.requiresSignature ? signatureData : null,
        metadata: {
          browser: navigator.userAgent,
          platform: navigator.platform,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      await onAcknowledge?.(acknowledgmentData);
      setAcknowledged(true);
      setShowSignature(false);
    } catch (error) {
      console.error('Failed to acknowledge policy:', error);
    } finally {
      setIsAcknowledging(false);
    }
  };

  const handleQuickAcknowledge = async () => {
    if (policy.requiresSignature) {
      setShowSignature(true);
    } else {
      await handleAcknowledge(null);
    }
  };

  return (
    <Card className={`transition-all ${acknowledged ? 'border-green-200 bg-green-50/30' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`
              w-10 h-10 rounded-lg flex items-center justify-center
              ${acknowledged ? 'bg-green-100' : 'bg-blue-100'}
            `}>
              {acknowledged ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <FileText className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <CardTitle className="text-base">{policy.title}</CardTitle>
              <CardDescription className="text-xs mt-1">
                {policy.category} • {policy.jurisdiction || 'Company-Wide'}
                {policy.version && ` • v${policy.version}`}
              </CardDescription>
            </div>
          </div>

          {acknowledged ? (
            <Badge className="bg-green-100 text-green-800 border-green-200">
              <Check className="h-3 w-3 mr-1" />
              Acknowledged
            </Badge>
          ) : policy.requiresSignature ? (
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
              <Pen className="h-3 w-3 mr-1" />
              Signature Required
            </Badge>
          ) : (
            <Badge variant="outline">
              <Eye className="h-3 w-3 mr-1" />
              Read & Acknowledge
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {/* Existing Acknowledgment Details */}
        {existingAcknowledgment && (
          <div className="p-3 bg-green-50 rounded-lg border border-green-100 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-green-700">
                Acknowledged on {new Date(existingAcknowledgment.acknowledgedAt).toLocaleString()}
              </span>
              {existingAcknowledgment.signature && (
                <SignatureDisplay signature={existingAcknowledgment.signature} showDetails={false} />
              )}
            </div>
          </div>
        )}

        {/* Read Policy Button/Dialog */}
        {!acknowledged && (
          <Dialog open={isReading} onOpenChange={setIsReading}>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full" disabled={disabled}>
                <BookOpen className="h-4 w-4 mr-2" />
                Read Policy
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {policy.title}
                </DialogTitle>
                <DialogDescription>
                  Please read the entire document before acknowledging
                </DialogDescription>
              </DialogHeader>

              <PolicyReader
                content={policy.content}
                title={policy.title}
                onReadComplete={() => setHasRead(true)}
                readComplete={hasRead}
                requireFullScroll={true}
              />

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Shield className="h-4 w-4" />
                  <span>Your acknowledgment will be securely recorded</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setIsReading(false)}>
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      setIsReading(false);
                      handleQuickAcknowledge();
                    }}
                    disabled={!hasRead || disabled}
                  >
                    {policy.requiresSignature ? (
                      <>
                        <Pen className="h-4 w-4 mr-2" />
                        Sign & Acknowledge
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        I Acknowledge
                      </>
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Signature Dialog */}
        <Dialog open={showSignature} onOpenChange={setShowSignature}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <ESignature
              documentTitle={policy.title}
              documentType="policy_acknowledgment"
              documentId={policy.policyCode}
              signerId={employeeId}
              signerName={employeeName}
              signerEmail={employeeEmail}
              onSign={handleAcknowledge}
              onCancel={() => setShowSignature(false)}
              disabled={isAcknowledging}
            />
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MULTIPLE POLICIES ACKNOWLEDGMENT PAGE
// ============================================================================

export default function PolicyAcknowledgment({
  policies = [],
  policy, // Single policy (alternative to policies array)
  employee, // Employee object (preferred)
  employeeId: propEmployeeId, // Legacy: individual ID
  employeeName: propEmployeeName, // Legacy: individual name
  employeeEmail: propEmployeeEmail, // Legacy: individual email
  existingAcknowledgments = [],
  existingAcknowledgment, // Single acknowledgment (for single policy mode)
  onAcknowledge,
  onComplete,
  onCancel,
  title = 'Policy Acknowledgments',
  description = 'Please review and acknowledge the following policies',
  showProgress = true,
  className = '',
}) {
  // Derive employee data from either employee object or individual props
  const employeeId = employee?.id || propEmployeeId;
  const employeeName = employee?.profile?.firstName
    ? `${employee.profile.firstName} ${employee.profile.lastName || ''}`.trim()
    : employee?.name || propEmployeeName || '';
  const employeeEmail = employee?.email || propEmployeeEmail;

  // Handle single policy mode
  const policyList = policy ? [policy] : policies;
  const initialAcks = existingAcknowledgment
    ? { [policy?.policyCode]: existingAcknowledgment }
    : existingAcknowledgments.reduce((acc, ack) => {
      acc[ack.policyCode] = ack;
      return acc;
    }, {});

  const [acknowledgments, setAcknowledgments] = useState(initialAcks);

  const acknowledgedCount = Object.keys(acknowledgments).length;
  const totalCount = policyList.length;
  const progress = totalCount > 0 ? (acknowledgedCount / totalCount) * 100 : 0;
  const allComplete = acknowledgedCount === totalCount;

  const handleAcknowledge = async (acknowledgmentData) => {
    await onAcknowledge?.(acknowledgmentData);
    setAcknowledgments((prev) => ({
      ...prev,
      [acknowledgmentData.policyCode]: acknowledgmentData,
    }));
  };

  useEffect(() => {
    if (allComplete) {
      onComplete?.();
    }
  }, [allComplete, onComplete]);

  // Group policies by category
  const groupedPolicies = policyList.reduce((acc, policy) => {
    const category = policy.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(policy);
    return acc;
  }, {});

  const categoryLabels = {
    state_notices: 'State Notices',
    policy: 'Company Policies',
    agreement: 'Agreements',
    company_policy: 'Company Policies',
    other: 'Other Documents',
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Scroll className="h-6 w-6 text-blue-600" />
          {title}
        </h2>
        <p className="text-gray-600">{description}</p>
      </div>

      {/* Progress */}
      {showProgress && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Acknowledgment Progress
              </span>
              <span className="text-sm text-gray-600">
                {acknowledgedCount} of {totalCount} completed
              </span>
            </div>
            <Progress value={progress} className="h-3" />
            {allComplete && (
              <div className="mt-3 flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">All policies acknowledged!</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Policies by Category */}
      {Object.entries(groupedPolicies).map(([category, categoryPolicies]) => (
        <div key={category} className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            {categoryLabels[category] || category}
            <Badge variant="outline" className="ml-2">
              {categoryPolicies.filter((p) => acknowledgments[p.policyCode]).length}/
              {categoryPolicies.length}
            </Badge>
          </h3>

          <div className="grid gap-3">
            {categoryPolicies.map((policy) => (
              <PolicyAcknowledgmentCard
                key={policy.policyCode}
                policy={policy}
                employeeId={employeeId}
                employeeName={employeeName}
                employeeEmail={employeeEmail}
                onAcknowledge={handleAcknowledge}
                existingAcknowledgment={acknowledgments[policy.policyCode]}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Empty State */}
      {policyList.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">All caught up!</h3>
            <p className="text-gray-500 mt-1">No pending policy acknowledgments.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// NOTICE ACKNOWLEDGMENT (Simpler - Just Read & Confirm)
// ============================================================================

export function NoticeAcknowledgment({
  notice,
  employeeId,
  employeeName,
  onAcknowledge,
  existingAcknowledgment,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasRead, setHasRead] = useState(!!existingAcknowledgment);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [acknowledged, setAcknowledged] = useState(!!existingAcknowledgment);

  const handleAcknowledge = async () => {
    setIsAcknowledging(true);
    try {
      const acknowledgmentData = {
        id: uuidv4(),
        noticeCode: notice.noticeCode,
        noticeTitle: notice.title,
        employeeId,
        employeeName,
        acknowledgedAt: new Date().toISOString(),
        metadata: {
          browser: navigator.userAgent,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      await onAcknowledge?.(acknowledgmentData);
      setAcknowledged(true);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to acknowledge notice:', error);
    } finally {
      setIsAcknowledging(false);
    }
  };

  return (
    <div className={`
      p-4 rounded-lg border transition-all
      ${acknowledged ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 hover:border-blue-200'}
    `}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {acknowledged ? (
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          ) : (
            <Info className="h-5 w-5 text-blue-600" />
          )}
          <div>
            <h4 className="font-medium text-gray-900">{notice.title}</h4>
            <p className="text-xs text-gray-500">
              {notice.legalReference}
            </p>
          </div>
        </div>

        {acknowledged ? (
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-800 border-green-200">
              <Check className="h-3 w-3 mr-1" />
              Acknowledged
            </Badge>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(true)}>
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={disabled}>
                <Eye className="h-4 w-4 mr-2" />
                Review
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>{notice.title}</DialogTitle>
                <DialogDescription>{notice.legalReference}</DialogDescription>
              </DialogHeader>

              <PolicyReader
                content={notice.content}
                onReadComplete={() => setHasRead(true)}
                readComplete={hasRead}
                requireFullScroll={notice.requiresAck}
              />

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsOpen(false)}>
                  Close
                </Button>
                <Button
                  onClick={handleAcknowledge}
                  disabled={!hasRead || isAcknowledging || disabled}
                >
                  {isAcknowledging ? (
                    'Acknowledging...'
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      I Acknowledge Receipt
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {existingAcknowledgment && (
        <p className="text-xs text-green-600 mt-2">
          Acknowledged on {new Date(existingAcknowledgment.acknowledgedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// BULK NOTICE ACKNOWLEDGMENT
// ============================================================================

export function BulkNoticeAcknowledgment({
  notices = [],
  employeeId,
  employeeName,
  existingAcknowledgments = [],
  onAcknowledge,
  onComplete,
  title = 'Required Notices',
}) {
  const [acknowledgments, setAcknowledgments] = useState(
    existingAcknowledgments.reduce((acc, ack) => {
      acc[ack.noticeCode] = ack;
      return acc;
    }, {})
  );

  const acknowledgedCount = Object.keys(acknowledgments).length;
  const allComplete = acknowledgedCount === notices.length;

  const handleAcknowledge = async (data) => {
    await onAcknowledge?.(data);
    setAcknowledgments((prev) => ({
      ...prev,
      [data.noticeCode]: data,
    }));
  };

  useEffect(() => {
    if (allComplete && notices.length > 0) {
      onComplete?.();
    }
  }, [allComplete, onComplete, notices.length]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <Badge variant="outline">
          {acknowledgedCount}/{notices.length} acknowledged
        </Badge>
      </div>

      <div className="space-y-2">
        {notices.map((notice) => (
          <NoticeAcknowledgment
            key={notice.noticeCode}
            notice={notice}
            employeeId={employeeId}
            employeeName={employeeName}
            onAcknowledge={handleAcknowledge}
            existingAcknowledgment={acknowledgments[notice.noticeCode]}
          />
        ))}
      </div>
    </div>
  );
}
